/**
 * request_access — read the machine-readable remediation a 403 carries so a
 * locked resource ends in "Request access", not a dead-end Retry.
 *
 * The backend attaches an RFC 9457 extension `{request_access:{resource,
 * allowed_relations}}` to the 403 for resource types that HAVE an access-request
 * flow (sessions, documents, units) — see api-service-go/middleware.go
 * (WriteProblemWithExtensions). The client surfaces it on ApiError.extensions;
 * this parses + validates the shape (never trusting the wire blindly).
 */

import { ApiError } from '@/api/client';

export interface RequestAccessRemediation {
  /** The locked resource as "<type>:<id>". */
  resource: string;
  /** Relations the caller may request on it (e.g. ['viewer','editor']). */
  allowed_relations: string[];
}

/** Parsed remediation, split into its parts, or null when the 403 carried none. */
export interface ParsedRequestAccess {
  resourceType: string;
  resourceId: string;
  allowedRelations: string[];
}

function isRemediation(v: unknown): v is RequestAccessRemediation {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.resource === 'string' &&
    Array.isArray(r.allowed_relations) &&
    r.allowed_relations.every((x) => typeof x === 'string')
  );
}

/**
 * Extract the request-access remediation from a 403 error, or null. Only a 403
 * with a well-formed `request_access` extension yields a value — any other error
 * (or a bare 403 without the flow) returns null, and the caller keeps its
 * ordinary error handling.
 */
export function getRequestAccess(err: unknown): ParsedRequestAccess | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const raw = err.extensions?.request_access;
  if (!isRemediation(raw)) return null;
  const sep = raw.resource.indexOf(':');
  if (sep <= 0 || sep >= raw.resource.length - 1) return null;
  return {
    resourceType: raw.resource.slice(0, sep),
    resourceId: raw.resource.slice(sep + 1),
    allowedRelations: raw.allowed_relations,
  };
}
